/**
 * Havnby vehicle context for Shopify Impact + EasySearch.
 *
 * What it does:
 * - Saves a completed EasySearch vehicle selection.
 * - Activates personalization only for the current browser tab/session.
 * - Keeps Shopify product URLs unchanged.
 * - Personalizes configured product-card and PDP titles.
 * - Preselects the matching Shopify variant on personalized PDP visits.
 * - Adds the selected vehicle as a Shopify line-item property.
 * - Restores original Shopify titles when the context is cleared/expired.
 * - Handles Impact AJAX/section rendering without repeatedly thrashing the DOM.
 *
 * Important:
 * - EasySearch remains the source of truth for compatibility.
 * - EasySearch vehicle values must match the visible Shopify option labels.
 * - Add every product that should receive a personalized title to PRODUCTS.
 */
(() => {
  'use strict';

  if (window.location.pathname === '/products/cloudcore-seamless-support-mattress-for-suv') {
    return;
  }

  if (window.HavnbyVehicleContext?.initialized) return;

  const RUNTIME_VERSION = '9.6.2';

  const CONFIG = Object.freeze({
    vehicleStorageKey: 'havnby_selected_vehicle_v3',
    // Version 9 carries the exact filtered vehicle across PDP navigation.
    // Changing the key also safely invalidates unscoped state from older code.
    contextStorageKey: 'havnby_vehicle_context_v9',

    // Remember the last selected vehicle for seven days. Merely remembering a
    // vehicle does not activate personalized titles in a new tab.
    vehicleMaxAgeMs: 7 * 24 * 60 * 60 * 1000,

    // Stop personalizing an abandoned tab after four hours.
    contextMaxAgeMs: 4 * 60 * 60 * 1000,

    fieldIds: Object.freeze({
      make: 'easysearch_field_35716',
      model: 'easysearch_field_35717',
      year: 'easysearch_field_35715'
    }),

    searchButtonSelector: '#easysearch-search',
    mainSelector: 'main',
    productCardLinkSelector: 'product-card .product-card__title a',
    anyProductCardLinkSelector: 'product-card a[href*="/products/"]',
    collectionTitleSelector: '[data-havnby-collection-title], .collection-header h1, .collection__title',
    productTitleSelector: '[data-havnby-product-title], .product-info__title',
    addToCartFormSelector: 'form[action*="/cart/add"]',
    fitmentClass: 'havnby-vehicle-fit',
    sizeClass: 'havnby-vehicle-size'
  });

  const VEHICLE_QUERY_PARAM = 'havnby_vehicle';
  const VEHICLE_HASH_PREFIX = 'havnby-vehicle=';
  const initialUrl = new URL(window.location.href);
  const initialProductPath = initialUrl.pathname.match(
    /\/products\/[^/?#]+/i
  )?.[0] || '';
  let hasSessionVehicleHandoff = false;

  try {
    const initialContext = JSON.parse(
      sessionStorage.getItem(CONFIG.contextStorageKey)
    );

    hasSessionVehicleHandoff = Boolean(
      initialContext?.pdpMode === 'filtered' &&
      initialContext?.productPath === initialProductPath
    );
  } catch (_error) {
    hasSessionVehicleHandoff = false;
  }

  const hasInitialVehicleHandoff = Boolean(
    initialUrl.searchParams.has(VEHICLE_QUERY_PARAM) ||
    initialUrl.hash.replace(/^#/, '').startsWith(VEHICLE_HASH_PREFIX) ||
    hasSessionVehicleHandoff
  );

  document.documentElement.dataset.havnbyVehicleRuntime = RUNTIME_VERSION;

  if (hasInitialVehicleHandoff) {
    document.documentElement.setAttribute(
      'data-havnby-vehicle-picker-locked',
      'true'
    );
  }

  /**
   * Active vehicle/fitment products in the Havnby Shopify catalog.
   * Extra Shipping Fee and the camping lantern are intentionally excluded.
   */
  const PRODUCTS = Object.freeze({
    '/products/cloudcore-diagonal-mattress-for-truck': Object.freeze({ baseTitle: 'CloudCore Diagonal Mattress' }),
    '/products/cloudcore-hybrid-support-mattress-for-rivian-r2': Object.freeze({ baseTitle: 'CloudCore Hybrid Support Mattress' }),
    '/products/cloudcore-hybrid-support-mattress-for-tesla-model-s-3-x-y': Object.freeze({ baseTitle: 'CloudCore Hybrid Support Mattress' }),
    '/products/cloudcore-foam-mattress-for-volkswagen-t-roc': Object.freeze({ baseTitle: 'CloudCore Hybrid Support Mattress' }),
    '/products/dreamshade-car-window-mesh-screens': Object.freeze({ baseTitle: 'DreamShade Car Window Mesh Screens' }),
    '/products/dreamshade-car-window-sun-shade-for-tesla-model-yl': Object.freeze({ baseTitle: 'DreamShade Car Window Sun Shade' }),
    '/products/dreamshade-car-window-sun-shade-for-tesla-model-y-3': Object.freeze({ baseTitle: 'DreamShade Car Window Sun Shade' }),
    '/products/flatcore-hybrid-support-front-seat-mattress-for-tesla-model-yl': Object.freeze({ baseTitle: 'FlatCore Hybrid Support Front Seat Mattress' }),
    '/products/flatcore-hybrid-support-mattress-for-honda-cr-v': Object.freeze({ baseTitle: 'FlatCore Hybrid Support Mattress' }),
    '/products/flatcore-hybrid-support-mattress-for-tesla-model-y': Object.freeze({ baseTitle: 'FlatCore Hybrid Support Mattress' }),
    '/products/flatcore-hybrid-support-mattress-for-tesla-model-y-solo-edition': Object.freeze({ baseTitle: 'FlatCore Hybrid Support Mattress - Solo Edition' }),
    '/products/flatcore-hybrid-support-mattress-for-tesla-model-yl': Object.freeze({ baseTitle: 'FlatCore Hybrid Support Mattress' }),
    '/products/flatcore-hybrid-support-mattress-for-toyota-rav4': Object.freeze({ baseTitle: 'FlatCore Hybrid Support Mattress' }),
        '/products/cloudcore-seamless-support-mattress-for-suv': Object.freeze({ baseTitle: 'CloudCore Seamless Support Mattress' }),
    '/products/flatcore-hybrid-support-mattress-for-toyota-sienna': Object.freeze({ baseTitle: 'FlatCore Hybrid Support Mattress' }),
    '/products/mininest-third-row-sleeping-pad-for-tesla-model-yl': Object.freeze({ baseTitle: 'MiniNest Third-Row Sleeping Pad' }),
    '/products/mininest-third-row-sleeping-pad-for-toyota-highlander': Object.freeze({ baseTitle: 'MiniNest Third-Row Sleeping Pad' }),
    '/products/perfect-fit-bed-sheet-for-cloudcore-tesla-mattress': Object.freeze({ baseTitle: 'Perfect Fit Bed Sheet' }),
    '/products/snugnest-back-seat-air-sleeping-pad-for-suv': Object.freeze({ baseTitle: 'SnugNest Back Seat Air Sleeping Pad' }),
    '/products/snugnest-back-seat-foam-top-sleeping-pad-for-truck': Object.freeze({ baseTitle: 'SnugNest Back Seat Foam-Top Sleeping Pad' }),
    '/products/snugnest-back-seat-foam-top-sleeping-pad-for-mid-size-trucks': Object.freeze({ baseTitle: 'SnugNest Back Seat Foam-Top Sleeping Pad' }),
    '/products/snugnest-back-seat-foam-top-sleeping-pad-for-suv': Object.freeze({ baseTitle: 'SnugNest Back Seat Foam-Top Sleeping Pad' }),
    '/products/snugnest-inflatable-extension-pad-for-truck': Object.freeze({ baseTitle: 'SnugNest Inflatable Extension Pad' }),
    '/products/snugnest-second-row-sleeping-pad-for-subaru-outback-forester': Object.freeze({ baseTitle: 'SnugNest Second-Row Sleeping Pad' }),
    '/products/snugnest-second-row-sleeping-pad-for-toyota-highlander': Object.freeze({ baseTitle: 'SnugNest Second-Row Sleeping Pad' }),
    '/products/snugnest-third-row-sleeping-pad-for-minivans': Object.freeze({ baseTitle: 'SnugNest Third-Row Sleeping Pad' }),
    '/products/snugnest-truck-sleep-system': Object.freeze({ baseTitle: 'SnugNest Truck Sleep System' }),
    '/products/used-flatcore-hybrid-support-mattress-for-honda-cr-v': Object.freeze({ baseTitle: '[Used] FlatCore Hybrid Support Mattress' }),
    '/products/used-flatcore-hybrid-support-mattress-for-tesla-model-y': Object.freeze({ baseTitle: '[Used] FlatCore Hybrid Support Mattress' }),
    '/products/used-flatcore-foam-mattress-for-toyota-rav4': Object.freeze({ baseTitle: '[Used] FlatCore Hybrid Support Mattress' }),
    '/products/used-snugnest-back-seat-air-sleeping-pad-for-suv': Object.freeze({ baseTitle: '[Used] SnugNest Back Seat Air Sleeping Pad' }),
    '/products/used-snugnest-back-seat-foam-top-sleeping-pad-for-truck': Object.freeze({ baseTitle: '[Used] SnugNest Back Seat Foam-Top Sleeping Pad' })
  });

  // Product-specific fitment fields that are not part of EasySearch's
  // Make/Model/Year selection. SnugNest mid-size truck pads use two physical
  // lengths; the vehicle database is the source for this compact runtime map.
  const PRODUCT_SIZE_BY_MODEL = Object.freeze({
    '/products/snugnest-back-seat-foam-top-sleeping-pad-for-mid-size-trucks': Object.freeze({
      'colorado': '135 cm',
      'montana': '135 cm',
      'strada': '135 cm',
      'toro': '135 cm',
      'ranger': '135 cm',
      'maverick': '135 cm',
      'canyon': '135 cm',
      'cannon': '135 cm',
      'santa cruz': '135 cm',
      'd-max': '135 cm',
      'gladiator': '135 cm',
      'tasman': '135 cm',
      'triton': '135 cm',
      'l200': '135 cm',
      'frontier': '135 cm',
      'navara': '135 cm',
      'rampage': '135 cm',
      'oroch': '135 cm',
      'hilux': '135 cm',
      'tacoma': '135 cm',
      'amarok': '135 cm',
      'shanhai poer': '145 cm',
      'ridgeline': '145 cm',
      'r1t': '145 cm',
      'shark 6': '145 cm'
    })
  });

  const originalText = new WeakMap();
  let renderScheduled = false;
  let observer = null;
  let variantSyncTimer = null;
  let variantSyncKey = '';
  let variantSyncAttempts = 0;
  let variantSyncComplete = false;
  let variantSyncCancelled = false;
  let variantStabilityTimer = null;
  let variantPickerLockTimer = null;
  let variantApplyTimer = null;
  let pendingVariant = null;
  let fitmentUpdatingUntil = 0;
  let fitmentTransitionTimer = null;
  const MAX_VARIANT_SYNC_ATTEMPTS = 18;
  const MAX_UNRESOLVED_VARIANT_ATTEMPTS = 12;

  function now() {
    return Date.now();
  }

  function releaseVariantPickerLock() {
    document.documentElement.removeAttribute(
      'data-havnby-vehicle-picker-locked'
    );

    if (variantPickerLockTimer) {
      window.clearTimeout(variantPickerLockTimer);
      variantPickerLockTimer = null;
    }
  }

  if (hasInitialVehicleHandoff) {
    variantPickerLockTimer = window.setTimeout(
      releaseVariantPickerLock,
      1800
    );
  }

  function safeParse(value) {
    if (!value) return null;

    try {
      return JSON.parse(value);
    } catch (_error) {
      return null;
    }
  }

  function storageGet(storage, key) {
    try {
      return safeParse(storage.getItem(key));
    } catch (_error) {
      return null;
    }
  }

  function storageSet(storage, key, value) {
    try {
      storage.setItem(key, JSON.stringify(value));
      return true;
    } catch (_error) {
      return false;
    }
  }

  function storageRemove(storage, key) {
    try {
      storage.removeItem(key);
    } catch (_error) {
      // Storage can be unavailable in restricted/private browser contexts.
    }
  }

  function cleanValue(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function isPlaceholder(value) {
    const normalized = cleanValue(value).toLowerCase();

    return (
      !normalized ||
      normalized === 'make' ||
      normalized === 'model' ||
      normalized === 'year' ||
      normalized.startsWith('select ')
    );
  }

  function getFieldValue(id) {
    const field = document.getElementById(id);
    if (!field) return '';

    // For native selects, visible option text is safer than an internal option ID.
    if (field instanceof HTMLSelectElement) {
      const option = field.selectedOptions?.[0];
      const text = cleanValue(option?.textContent);

      if (!isPlaceholder(text)) return text;
    }

    const value = cleanValue(field.value);
    return isPlaceholder(value) ? '' : value;
  }

  function normalizeVehicle(vehicle) {
    if (!vehicle) return null;

    const normalized = {
      year: cleanValue(vehicle.year),
      make: cleanValue(vehicle.make),
      model: cleanValue(vehicle.model)
    };

    if (!normalized.year || !normalized.make || !normalized.model) {
      return null;
    }

    return normalized;
  }

  function readVehicleFromWidget() {
    return normalizeVehicle({
      make: getFieldValue(CONFIG.fieldIds.make),
      model: getFieldValue(CONFIG.fieldIds.model),
      year: getFieldValue(CONFIG.fieldIds.year)
    });
  }

  function saveRememberedVehicle(vehicle) {
    const normalized = normalizeVehicle(vehicle);
    if (!normalized) return false;

    return storageSet(localStorage, CONFIG.vehicleStorageKey, {
      version: 1,
      ...normalized,
      savedAt: now()
    });
  }

  function getRememberedVehicle() {
    const record = storageGet(localStorage, CONFIG.vehicleStorageKey);
    const vehicle = normalizeVehicle(record);

    if (
      !vehicle ||
      !Number.isFinite(record?.savedAt) ||
      now() - record.savedAt > CONFIG.vehicleMaxAgeMs
    ) {
      storageRemove(localStorage, CONFIG.vehicleStorageKey);
      return null;
    }

    return vehicle;
  }

  function activateContext(vehicle, { deferCollectionScope = false } = {}) {
    const normalized = normalizeVehicle(vehicle);
    if (!normalized) return false;

    const timestamp = now();

    return storageSet(sessionStorage, CONFIG.contextStorageKey, {
      version: 1,
      vehicle: normalized,
      activatedAt: timestamp,
      lastSeenAt: timestamp,
      // Search may redirect from the homepage or from one collection route to
      // another. In that case the destination listing claims the scope after
      // it actually loads instead of locking the source page too early.
      collectionPath: deferCollectionScope
        ? ''
        : getCollectionPath(window.location.href),
      productPath: null,
      pdpMode: null,
      pdpVehicle: null,
      variantTitle: null,
      variantMatchStatus: null
    });
  }

  function getContext({ touch = true } = {}) {
    const context = storageGet(sessionStorage, CONFIG.contextStorageKey);
    const vehicle = normalizeVehicle(context?.vehicle);

    if (
      !vehicle ||
      !Number.isFinite(context?.activatedAt) ||
      !Number.isFinite(context?.lastSeenAt) ||
      now() - context.lastSeenAt > CONFIG.contextMaxAgeMs
    ) {
      storageRemove(sessionStorage, CONFIG.contextStorageKey);
      return null;
    }

    const validContext = {
      ...context,
      vehicle
    };

    if (touch && now() - context.lastSeenAt > 60 * 1000) {
      validContext.lastSeenAt = now();
      storageSet(sessionStorage, CONFIG.contextStorageKey, validContext);
    }

    return validContext;
  }

  function updateContext(updates) {
    const context = getContext({ touch: false });
    if (!context) return false;

    return storageSet(sessionStorage, CONFIG.contextStorageKey, {
      ...context,
      ...updates,
      lastSeenAt: now()
    });
  }

  function saveAndActivateVehicle(options = {}) {
    const vehicle = readVehicleFromWidget();
    if (!vehicle) return null;

    saveRememberedVehicle(vehicle);
    activateContext(vehicle, options);

    // If selection happens directly on a configured PDP, allow that PDP.
    const currentProductPath = getProductPath(window.location.href);
    if (PRODUCTS[currentProductPath]) {
      updateContext({
        productPath: currentProductPath,
        pdpMode: 'filtered',
        pdpVehicle: null,
        variantTitle: null,
        variantMatchStatus: null
      });
    }

    return vehicle;
  }

  function addVehicleToProductLink(link, vehicle) {
    const normalized = normalizeVehicle(vehicle);
    if (!normalized) return;

    const url = new URL(link.href, window.location.origin);
    url.searchParams.set(VEHICLE_QUERY_PARAM, JSON.stringify(normalized));

    if (url.hash.replace(/^#/, '').startsWith(VEHICLE_HASH_PREFIX)) {
      url.hash = '';
    }

    link.href = url.toString();
  }

  function removeVehicleFromProductLink(link) {
    const url = new URL(link.href, window.location.origin);
    const hash = url.hash.replace(/^#/, '');
    const hasQueryVehicle = url.searchParams.has(VEHICLE_QUERY_PARAM);
    const hasHashVehicle = hash.startsWith(VEHICLE_HASH_PREFIX);

    if (!hasQueryVehicle && !hasHashVehicle) return;

    url.searchParams.delete(VEHICLE_QUERY_PARAM);
    if (hasHashVehicle) url.hash = '';
    link.href = url.toString();
  }

  function claimVehicleFromProductUrl() {
    const url = new URL(window.location.href);
    const hash = window.location.hash.replace(/^#/, '');
    const queryVehicle = url.searchParams.get(VEHICLE_QUERY_PARAM);
    const hashVehicle = hash.startsWith(VEHICLE_HASH_PREFIX)
      ? hash.slice(VEHICLE_HASH_PREFIX.length)
      : '';

    if (!queryVehicle && !hashVehicle) return null;

    let vehicle = null;

    try {
      vehicle = normalizeVehicle(JSON.parse(
        queryVehicle || decodeURIComponent(hashVehicle)
      ));
    } catch (_error) {
      vehicle = null;
    }

    if (vehicle) {
      saveRememberedVehicle(vehicle);
      activateContext(vehicle);
      updateContext({
        productPath: getProductPath(window.location.href),
        pdpMode: 'filtered',
        pdpVehicle: null,
        variantTitle: null,
        variantMatchStatus: null
      });
    }

    url.searchParams.delete(VEHICLE_QUERY_PARAM);
    if (hashVehicle) url.hash = '';

    try {
      window.history.replaceState(
        window.history.state,
        '',
        `${url.pathname}${url.search}${url.hash}`
      );
    } catch (_error) {}

    return vehicle;
  }

  function clearVehicleContext({ forgetVehicle = true } = {}) {
    storageRemove(sessionStorage, CONFIG.contextStorageKey);

    if (forgetVehicle) {
      storageRemove(localStorage, CONFIG.vehicleStorageKey);
    }

    scheduleRender();
  }

  function getProductPath(url) {
    try {
      const pathname = new URL(url, window.location.origin).pathname;
      const match = pathname.match(/\/products\/[^/?#]+/i);
      return match ? match[0].replace(/\/$/, '') : '';
    } catch (_error) {
      return '';
    }
  }

  function getCollectionPath(url) {
    try {
      const pathname = new URL(url, window.location.origin).pathname;
      const collectionMatch = pathname.match(
        /\/collections(?:\/[^/?#]+)?/i
      );

      if (collectionMatch) {
        return collectionMatch[0].replace(/\/$/, '');
      }

      // Some EasySearch configurations use Shopify's search results route.
      if (/\/search\/?$/i.test(pathname)) return '/search';

      return '';
    } catch (_error) {
      return '';
    }
  }

  function getDisplayTitle(product, vehicle) {
    return `${product.baseTitle} for ${getVehicleLabel(vehicle)}`;
  }

  function getVehicleLabel(vehicle) {
    return [vehicle?.year, vehicle?.make, vehicle?.model]
      .map(cleanValue)
      .filter(Boolean)
      .join(' ');
  }

  function rememberOriginalText(element) {
    if (!originalText.has(element)) {
      originalText.set(element, element.textContent.trim());
    }
  }

  function setElementText(element, value) {
    if (!element) return;

    rememberOriginalText(element);

    if (element.textContent.trim() !== value) {
      element.textContent = value;
    }
  }

  function restoreElementText(element) {
    if (!element || !originalText.has(element)) return;

    const value = originalText.get(element);

    if (element.textContent.trim() !== value) {
      element.textContent = value;
    }
  }

  function isCollectionPage() {
    return Boolean(getCollectionPath(window.location.href));
  }

  function canPersonalizeCurrentCollection(context) {
    if (!context || !isCollectionPage()) return false;

    const currentCollectionPath = getCollectionPath(window.location.href);

    // A context created immediately before EasySearch navigation is deliberately
    // unscoped. The first actual product-listing destination claims it.
    if (currentCollectionPath && !context.collectionPath) {
      // A filtered PDP handoff has a productPath. If the customer now clicks
      // Shop/another collection, this is ordinary navigation—not the pending
      // EasySearch destination—so never let that PDP vehicle claim the new
      // collection even if a menu click happened before this script observed it.
      if (context.productPath || context.pdpMode === 'filtered') return false;

      context.collectionPath = currentCollectionPath;
      updateContext({ collectionPath: currentCollectionPath });
    }

    return Boolean(
      currentCollectionPath &&
      context.collectionPath === currentCollectionPath
    );
  }

  function getCollectionTitleElement() {
    if (!isCollectionPage()) return null;

    const preferred = document.querySelector(CONFIG.collectionTitleSelector);
    if (preferred) return preferred;

    // Impact normally renders the collection heading as the first H1 in main.
    // This fallback also covers customized collection templates.
    return Array.from(document.querySelectorAll('main h1')).find(
      (heading) =>
        !heading.closest('product-card') &&
        !heading.closest('.product-info')
    ) || null;
  }

  function updateCollectionTitle(vehicle) {
    const title = getCollectionTitleElement();
    if (!title) return;

    rememberOriginalText(title);

    if (!vehicle) {
      restoreElementText(title);
      return;
    }

    const baseTitle = originalText.get(title) || 'Products';
    const vehicleName = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
    setElementText(title, `${baseTitle} for ${vehicleName}`);
  }

  function updateProductCards(vehicle) {
    document
      .querySelectorAll(CONFIG.anyProductCardLinkSelector)
      .forEach((link) => {
        if (!PRODUCTS[getProductPath(link.href)]) return;

        if (vehicle) {
          addVehicleToProductLink(link, vehicle);
        } else {
          removeVehicleFromProductLink(link);
        }
      });

    document
      .querySelectorAll(CONFIG.productCardLinkSelector)
      .forEach((link) => {
        const product = PRODUCTS[getProductPath(link.href)];
        if (!product) return;

        if (vehicle) {
          setElementText(link, getDisplayTitle(product, vehicle));
        } else {
          restoreElementText(link);
        }
      });
  }

  function getProductTitleTextElement() {
    const title = document.querySelector(CONFIG.productTitleSelector);
    if (!title) return null;

    // Optional inner span prevents removal of badges/markup inside the H1.
    return title.querySelector('[data-havnby-title-text]') || title;
  }

  function ensureSizeMessage() {
    let sizeMessage = document.querySelector(`.${CONFIG.sizeClass}`);

    if (!sizeMessage) {
      sizeMessage = document.createElement('div');
      sizeMessage.className = CONFIG.sizeClass;
      sizeMessage.setAttribute('role', 'status');
      sizeMessage.setAttribute('aria-live', 'polite');
      sizeMessage.innerHTML = '<span data-havnby-size-text></span>';
    }

    const pickerBlock =
      document.querySelector(
        '.product-info__block-item[data-block-type="variant-picker"]'
      ) ||
      document.querySelector('.product-info__variant-picker')?.closest(
        '.product-info__block-item'
      ) ||
      getVariantPicker();

    if (pickerBlock && sizeMessage.previousElementSibling !== pickerBlock) {
      pickerBlock.insertAdjacentElement('afterend', sizeMessage);
    }

    return sizeMessage;
  }

  function removeSizeMessage() {
    document.querySelector(`.${CONFIG.sizeClass}`)?.remove();
  }

  function ensureFitmentMessage(titleElement) {
    let fit = document.querySelector(`.${CONFIG.fitmentClass}`);

    if (!fit) {
      fit = document.createElement('div');
      fit.className = CONFIG.fitmentClass;
      fit.setAttribute('role', 'status');
      fit.setAttribute('aria-live', 'polite');
      fit.innerHTML = '<span data-havnby-fit-text></span>';

    }

    const pickerBlock =
      document.querySelector(
        '.product-info__block-item[data-block-type="variant-picker"]'
      ) ||
      document.querySelector('.product-info__variant-picker')?.closest(
        '.product-info__block-item'
      );

    const fallbackAnchor =
      titleElement.closest('.product-info__title') || titleElement;
    const placementAnchor =
      document.querySelector(`.${CONFIG.sizeClass}`) ||
      pickerBlock ||
      fallbackAnchor;

    // Reposition existing messages too, so upgrading from an older script does
    // not require a hard refresh to move the element away from the title.
    if (fit.previousElementSibling !== placementAnchor) {
      placementAnchor.insertAdjacentElement('afterend', fit);
    }

    return fit;
  }

  function removeFitmentMessage() {
    document.querySelector(`.${CONFIG.fitmentClass}`)?.remove();
  }

  function addVehicleProperties(form, vehicle, variantTitle = '') {
    const properties = {
      // Visible on the cart/order line item.
      Vehicle: getVehicleLabel(vehicle),

      // Shopify private line-item properties: available in order data while
      // hidden from the customer-facing checkout summary.
      '_Vehicle Year': vehicle.year,
      '_Vehicle Make': vehicle.make,
      '_Vehicle Model': vehicle.model,
      '_Vehicle Variant': cleanValue(variantTitle)
    };

    Object.entries(properties).forEach(([name, value]) => {
      const inputName = `properties[${name}]`;
      let input = Array.from(form.elements).find(
        (element) => element.name === inputName
      );

      if (!value) {
        if (input?.dataset.havnbyVehicleProperty === 'true') input.remove();
        return;
      }

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = inputName;
        input.dataset.havnbyVehicleProperty = 'true';
        form.appendChild(input);
      }

      input.value = value;
    });
  }

  function removeVehicleProperties(form) {
    form
      .querySelectorAll('[data-havnby-vehicle-property="true"]')
      .forEach((input) => input.remove());
  }

  function canPersonalizeCurrentPdp(context, productPath) {
    if (!context || !PRODUCTS[productPath]) return false;

    // The PDP is personalized only when it was entered from a personalized
    // product card, or the user selected a vehicle while already on this PDP.
    return (
      context.pdpMode === 'filtered' &&
      context.productPath === productPath
    );
  }

  function getProductDefaultMake(productPath) {
    const match = productPath.match(
      /-(tesla|toyota|honda|rivian|volkswagen|subaru)(?:-|$)/i
    );

    if (!match) return '';

    const makes = {
      tesla: 'Tesla',
      toyota: 'Toyota',
      honda: 'Honda',
      rivian: 'Rivian',
      volkswagen: 'Volkswagen',
      subaru: 'Subaru'
    };

    return makes[match[1].toLowerCase()] || '';
  }

  const KNOWN_MAKES = new Map([
    ['audi', 'Audi'], ['bmw', 'BMW'], ['byd', 'BYD'],
    ['cadillac', 'Cadillac'], ['chevrolet', 'Chevrolet'],
    ['chevolet', 'Chevrolet'], ['chrysler', 'Chrysler'],
    ['fiat', 'Fiat'], ['ford', 'Ford'], ['gmc', 'GMC'], ['gwm', 'GWM'],
    ['honda', 'Honda'], ['hyundai', 'Hyundai'], ['isuzu', 'Isuzu'],
    ['jeep', 'Jeep'], ['kia', 'Kia'], ['lexus', 'Lexus'],
    ['mazda', 'Mazda'], ['mercedes-benz', 'Mercedes-Benz'],
    ['mitsubishi', 'Mitsubishi'], ['nissan', 'Nissan'],
    ['ram', 'RAM'], ['renault', 'Renault'], ['rivian', 'Rivian'],
    ['skoda', 'Škoda'], ['subaru', 'Subaru'], ['tesla', 'Tesla'], ['toyota', 'Toyota'],
    ['volkswagen', 'Volkswagen'], ['volvo', 'Volvo']
  ]);

  function readSelectedVariant() {
    const picker =
      document.querySelector('main .product-info variant-picker') ||
      document.querySelector('main variant-picker');

    const script = picker?.querySelector('script[data-variant]');
    return safeParse(script?.textContent);
  }

  function getVariantOptions(variant) {
    if (!variant) return [];

    if (Array.isArray(variant.options)) {
      return variant.options.map(cleanValue).filter(Boolean);
    }

    return [variant.option1, variant.option2, variant.option3]
      .map(cleanValue)
      .filter(Boolean);
  }

  function getYearExpression(value) {
    const match = cleanValue(value).match(
      /\b((?:19|20)\d{2})(?:\s*[-–]\s*((?:19|20)?\d{2})|\+)?(?!\d)/
    );

    if (!match) return null;

    let end = match[2] || '';
    if (end && end.length === 2) end = `${match[1].slice(0, 2)}${end}`;

    return {
      text: match[0].replace(/\s+/g, ''),
      start: Number(match[1]),
      end: end ? Number(end) : null,
      openEnded: match[0].includes('+')
    };
  }

  function yearExpressionIncludes(yearExpression, yearValue) {
    const year = Number(yearValue);

    if (!yearExpression || !Number.isFinite(year)) return false;
    if (yearExpression.openEnded) return year >= yearExpression.start;

    if (yearExpression.end !== null) {
      return year >= yearExpression.start && year <= yearExpression.end;
    }

    return year === yearExpression.start;
  }

  function chooseDisplayYear(yearExpression, fallbackYear) {
    if (!yearExpression) return fallbackYear;

    const fitsSelectedYear = yearExpressionIncludes(
      yearExpression,
      fallbackYear
    );

    return fitsSelectedYear ? String(fallbackYear) : yearExpression.text;
  }

  function stripYear(value) {
    return cleanValue(value)
      .replace(/\b(?:19|20)\d{2}(?:\s*[-–]\s*(?:19|20)?\d{2}|\+)?(?!\d)/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function looksLikeVehicleModel(value) {
    const normalized = cleanValue(value);
    if (!normalized) return false;

    if (/^(gas|hybrid|phev|ev|default title|\d+\s*cm)/i.test(normalized)) {
      return false;
    }

    return (
      /^model\s+/i.test(normalized) ||
      /[a-z]/i.test(normalized)
    );
  }

  function deriveVehicleFromVariant(variant, fallbackVehicle) {
    const fallback = normalizeVehicle(fallbackVehicle);
    if (!fallback || !variant) return fallback;

    const options = getVariantOptions(variant);
    const title = cleanValue(variant.public_title || variant.title);
    const combined = [...options, title].join(' / ');
    const yearExpression = getYearExpression(combined);
    const vehicle = {
      ...fallback,
      year: chooseDisplayYear(yearExpression, fallback.year)
    };

    const segments = title
      .split('/')
      .map(cleanValue)
      .filter(Boolean);

    const makePrefix = Array.from(KNOWN_MAKES.entries())
      .sort((a, b) => b[0].length - a[0].length)
      .find(([make]) => {
        const lowerTitle = title.toLowerCase();
        return lowerTitle.startsWith(`${make} `) || lowerTitle.startsWith(`${make}/`);
      });

    if (makePrefix) {
      const [rawMake, displayMake] = makePrefix;
      const model = stripYear(
        title.slice(rawMake.length).replace(/^\s*\/\s*/, '')
      );

      vehicle.make = displayMake;
      if (model) vehicle.model = model;

      return normalizeVehicle(vehicle) || fallback;
    }

    const firstMake = KNOWN_MAKES.get(segments[0]?.toLowerCase());

    if (firstMake && segments[1]) {
      vehicle.make = firstMake;
      vehicle.model = stripYear(segments[1]) || fallback.model;
      return normalizeVehicle(vehicle) || fallback;
    }

    const optionMake = KNOWN_MAKES.get(options[0]?.toLowerCase());

    if (optionMake && options[1]) {
      vehicle.make = optionMake;
      vehicle.model = stripYear(options[1]) || fallback.model;
      return normalizeVehicle(vehicle) || fallback;
    }

    const modelCandidate = stripYear(options[0] || segments[0] || '');

    if (looksLikeVehicleModel(modelCandidate)) {
      // Do not replace a known model with a drivetrain/size-only option.
      if (!/^(gas|hybrid|phev|ev|default title)/i.test(modelCandidate)) {
        vehicle.model = modelCandidate;
      }
    }

    return normalizeVehicle(vehicle) || fallback;
  }

  function getVariantQualifier(variantTitle, vehicle) {
    let qualifier = cleanValue(variantTitle);
    if (!qualifier || /^default title$/i.test(qualifier)) return '';

    const removeParts = [vehicle.make, vehicle.model]
      .map(cleanValue)
      .filter(Boolean)
      .sort((a, b) => b.length - a.length);

    removeParts.forEach((part) => {
      qualifier = qualifier.replace(
        new RegExp(part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'ig'),
        ' '
      );
    });

    qualifier = qualifier
      .replace(/\b(?:19|20)\d{2}(?:\s*[-–]\s*(?:19|20)?\d{2}|\+)?(?!\d)/g, ' ')
      .replace(/[\/|]+/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/^[-–—\s]+|[-–—\s]+$/g, '')
      .trim();

    const normalizedQualifier = normalizeFitmentText(qualifier);
    const normalizedModel = normalizeFitmentText(stripYear(vehicle.model));

    if (!normalizedQualifier || normalizedQualifier === normalizedModel) {
      return '';
    }

    return qualifier;
  }

  function normalizeFitmentText(value) {
    return cleanValue(value)
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/chevolet/g, 'chevrolet')
      .replace(/[–—]/g, '-')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getVariantPicker() {
    return (
      document.querySelector('main .product-info variant-picker') ||
      document.querySelector('main variant-picker')
    );
  }

  function getOptionPurpose(fieldset) {
    const info = cleanValue(
      fieldset.querySelector('.variant-picker__option-info')?.textContent ||
      fieldset.querySelector('legend')?.textContent ||
      fieldset.getAttribute('data-option-name') ||
      ''
    ).toLowerCase();

    if (/\b(brand|make)\b/.test(info)) return 'make';
    if (/\bmodel\b/.test(info)) return 'model';
    if (/\byear\b/.test(info)) return 'year';
    if (/\b(size|length|dimension)\b/.test(info)) return 'size';
    return 'other';
  }

  function getProductSize(productPath, vehicle) {
    const modelMap = PRODUCT_SIZE_BY_MODEL[productPath];
    if (!modelMap || !vehicle) return '';

    const modelWithMakeRemoved = splitKnownMakePrefix(vehicle.model)?.model;
    const modelCandidates = [
      vehicle.model,
      modelWithMakeRemoved,
      stripYear(vehicle.model),
      stripYear(modelWithMakeRemoved)
    ]
      .map(normalizeFitmentText)
      .filter(Boolean);

    const mappedSize = modelCandidates
      .map((model) => modelMap[model])
      .find(Boolean);

    // Every other mid-size truck in this product uses the 135 cm pad.
    return mappedSize || '135 cm';
  }

  function getOptionChoices(fieldset) {
    return Array.from(
      fieldset.querySelectorAll(
        'label[for], [role="option"], button[data-option-value], option[value]'
      )
    ).filter((choice) => {
      if (choice.matches('button.select[aria-controls]')) return false;
      if (
        choice instanceof HTMLOptionElement &&
        isPlaceholder(choice.textContent)
      ) {
        return false;
      }
      return Boolean(cleanValue(choice.textContent));
    });
  }

  function getSelectedChoiceText(fieldset) {
    const choices = getOptionChoices(fieldset);
    const selectedChoice = choices.find(isChoiceSelected);

    if (selectedChoice) return cleanValue(selectedChoice.textContent);

    // Popover-style Impact pickers expose the current value in the trigger.
    return cleanValue(
      fieldset.querySelector('[id$="-selected-value"]')?.textContent || ''
    );
  }

  function restorePickerChoice(choice) {
    if (choice.dataset.havnbyHiddenFitmentChoice !== 'true') return;

    const originalDisplay = choice.dataset.havnbyOriginalDisplay || '';
    const originalPriority =
      choice.dataset.havnbyOriginalDisplayPriority || '';

    choice.hidden = false;

    if (originalDisplay) {
      choice.style.setProperty('display', originalDisplay, originalPriority);
    } else {
      choice.style.removeProperty('display');
    }

    choice.removeAttribute('data-havnby-hidden-fitment-choice');
    choice.removeAttribute('data-havnby-original-display');
    choice.removeAttribute('data-havnby-original-display-priority');
    choice.removeAttribute('aria-hidden');
  }

  function hidePickerChoice(choice) {
    if (choice.dataset.havnbyHiddenFitmentChoice === 'true') return;

    choice.dataset.havnbyOriginalDisplay =
      choice.style.getPropertyValue('display');
    choice.dataset.havnbyOriginalDisplayPriority =
      choice.style.getPropertyPriority('display');
    choice.hidden = true;
    choice.style.setProperty('display', 'none', 'important');
    choice.setAttribute('data-havnby-hidden-fitment-choice', 'true');
    choice.setAttribute('aria-hidden', 'true');
  }

  function updatePersonalizedPickerVisibility(context, productPath) {
    const picker = getVariantPicker();
    if (!picker) return;

    const previouslyHidden = Array.from(
      picker.querySelectorAll('[data-havnby-hidden-fitment-choice="true"]')
    );

    const shouldCollapse = canPersonalizeCurrentPdp(context, productPath);

    if (!shouldCollapse) {
      previouslyHidden.forEach(restorePickerChoice);
      picker.removeAttribute('data-havnby-personalized-fitment');
      return;
    }

    // Never hide choices while automatic selection is still in progress.
    // Impact may render the picker in several passes; hiding the non-target
    // labels before the checked inputs settle can leave an incorrect option
    // selected with the entire picker apparently missing.
    if (!pickerMatchesVehicle(picker, context.vehicle)) {
      previouslyHidden.forEach(restorePickerChoice);
      picker.removeAttribute('data-havnby-personalized-fitment');
      return;
    }

    picker.setAttribute('data-havnby-personalized-fitment', 'true');

    const keepHidden = new Set();

    buildVehicleSelectionPlan(picker, context.vehicle)
      .forEach(({ choices, matchingChoice }) => {
        if (!matchingChoice) return;

        choices.forEach((choice) => {
          if (choice === matchingChoice) {
            restorePickerChoice(choice);
            return;
          }

          keepHidden.add(choice);
          hidePickerChoice(choice);
        });
      });

    previouslyHidden.forEach((choice) => {
      if (!keepHidden.has(choice)) restorePickerChoice(choice);
    });
  }

  function splitKnownMakePrefix(value) {
    const rawValue = cleanValue(value);
    const normalizedValue = normalizeFitmentText(rawValue);
    const match = Array.from(KNOWN_MAKES.entries())
      .sort((a, b) => b[0].length - a[0].length)
      .find(([make]) =>
        normalizedValue === make || normalizedValue.startsWith(`${make} `)
      );

    if (!match) return null;

    const [makeKey, displayMake] = match;
    const tokens = rawValue.split(/\s+/);
    let model = '';

    for (let index = 1; index <= Math.min(tokens.length, 3); index += 1) {
      if (normalizeFitmentText(tokens.slice(0, index).join(' ')) === makeKey) {
        model = tokens
          .slice(index)
          .join(' ')
          .replace(/^\s*[\/|:-]+\s*/, '')
          .trim();
        break;
      }
    }

    return { make: displayMake, model };
  }

  function readFitmentFromSelectedOptions(fallbackVehicle) {
    const fallback = normalizeVehicle(fallbackVehicle);
    const picker = getVariantPicker();

    if (!fallback || !picker) return null;

    const vehicle = { ...fallback };
    const selectedValues = [];
    const fieldsets = Array.from(
      picker.querySelectorAll('fieldset.variant-picker__option, fieldset')
    );

    fieldsets.forEach((fieldset) => {
      const purpose = getOptionPurpose(fieldset);
      const selectedText = getSelectedChoiceText(fieldset);

      if (!selectedText) return;
      selectedValues.push(selectedText);

      if (purpose === 'make') {
        vehicle.make =
          KNOWN_MAKES.get(normalizeFitmentText(selectedText)) || selectedText;
      }

      if (purpose === 'model') {
        const combinedVehicle = splitKnownMakePrefix(selectedText);
        const selectedModel = stripYear(
          combinedVehicle?.model || selectedText
        );

        if (combinedVehicle) {
          const sameMakeAsFilter =
            normalizeFitmentText(combinedVehicle.make) ===
            normalizeFitmentText(fallback.make);

          vehicle.make = combinedVehicle.make;

          // During automatic entry, broad grouped options such as
          // "Ford Duty F-250/350/450" still represent the exact filtered model.
          // Once the customer manually switches, use the visible option instead.
          vehicle.model =
            !variantSyncCancelled && sameMakeAsFilter
              ? fallback.model
              : selectedModel || vehicle.model;
        } else if (
          normalizeFitmentText(selectedText).includes(
            normalizeFitmentText(fallback.model)
          )
        ) {
          vehicle.model = fallback.model;
        } else if (looksLikeVehicleModel(selectedModel)) {
          // Do not replace the filtered model with a drivetrain/generation
          // label such as "Gas (5th Gen)". Those labels identify a variant,
          // not a vehicle model, and must leave the EasySearch model intact.
          vehicle.model = selectedModel || vehicle.model;
        }

        const expression = getYearExpression(selectedText);
        if (expression) {
          vehicle.year = chooseDisplayYear(expression, vehicle.year);
        }
      }

      if (purpose === 'year') {
        const expression = getYearExpression(selectedText);
        vehicle.year = expression
          ? chooseDisplayYear(expression, selectedText)
          : selectedText;
      }
    });

    return {
      vehicle: normalizeVehicle(vehicle) || fallback,
      variantTitle: selectedValues.join(' / ')
    };
  }

  function readDirectPdpFitment(productPath) {
    const picker = getVariantPicker();
    if (!picker) return null;

    const vehicle = {
      year: '',
      make: getProductDefaultMake(productPath),
      model: ''
    };
    const selectedValues = [];
    const fieldsets = Array.from(
      picker.querySelectorAll('fieldset.variant-picker__option, fieldset')
    );

    fieldsets.forEach((fieldset) => {
      const purpose = getOptionPurpose(fieldset);
      const selectedText = getSelectedChoiceText(fieldset);
      if (!selectedText) return;

      selectedValues.push(selectedText);

      if (purpose === 'make') {
        vehicle.make =
          KNOWN_MAKES.get(normalizeFitmentText(selectedText)) || selectedText;
        return;
      }

      if (purpose === 'model') {
        const combinedVehicle = splitKnownMakePrefix(selectedText);
        if (combinedVehicle) {
          vehicle.make = combinedVehicle.make;
          vehicle.model = stripYear(combinedVehicle.model);
        } else {
          vehicle.model = stripYear(selectedText);
        }

        const expression = getYearExpression(selectedText);
        if (expression) vehicle.year = expression.text;
        return;
      }

      if (purpose === 'year') {
        vehicle.year = getYearExpression(selectedText)?.text || selectedText;
      }
    });

    if (!vehicle.model) {
      const combined = selectedValues
        .map((value) => ({ value, parsed: splitKnownMakePrefix(value) }))
        .find((entry) => entry.parsed?.model);

      if (combined) {
        vehicle.make = combined.parsed.make;
        vehicle.model = stripYear(combined.parsed.model);
        vehicle.year = getYearExpression(combined.value)?.text || vehicle.year;
      }
    }

    if (!vehicle.model) return null;

    return {
      vehicle,
      variantTitle: selectedValues.join(' / ')
    };
  }

  function readFixedProductFitment(productPath, titleElement) {
    rememberOriginalText(titleElement);

    const originalTitle = originalText.get(titleElement) || '';
    const suffix = cleanValue(originalTitle.split(/\s+for\s+/i).pop());
    if (!suffix || suffix === originalTitle) return null;

    const yearExpression = getYearExpression(suffix);
    const withoutYear = stripYear(suffix);
    const combinedVehicle = splitKnownMakePrefix(withoutYear);
    const defaultMake = getProductDefaultMake(productPath);
    const vehicle = {
      year: yearExpression?.text || '',
      make: combinedVehicle?.make || defaultMake,
      model: cleanValue(
        combinedVehicle?.model ||
        withoutYear.replace(new RegExp(`^${defaultMake}\\s+`, 'i'), '')
      )
    };

    if (!vehicle.make || !vehicle.model) return null;

    return { vehicle, variantTitle: '' };
  }

  function getChoiceInput(choice) {
    if (!(choice instanceof HTMLLabelElement) || !choice.htmlFor) return null;

    const escapedId = window.CSS?.escape
      ? window.CSS.escape(choice.htmlFor)
      : choice.htmlFor.replace(/([ #;&,.+*~':"!^$[\]()=>|/@])/g, '\\$1');

    return document.querySelector(`#${escapedId}`);
  }

  function isChoiceSelected(choice) {
    if (choice instanceof HTMLOptionElement) {
      return choice.selected;
    }

    const input = getChoiceInput(choice);
    if (input) return Boolean(input.checked);

    return (
      choice.getAttribute('aria-selected') === 'true' ||
      choice.getAttribute('aria-checked') === 'true' ||
      choice.classList.contains('is-selected')
    );
  }

  function yearChoiceMatches(choiceText, selectedYear) {
    const expression = getYearExpression(choiceText);
    const year = Number(selectedYear);

    if (!expression || !Number.isFinite(year)) {
      return normalizeFitmentText(choiceText) === normalizeFitmentText(selectedYear);
    }

    return yearExpressionIncludes(expression, year);
  }

  function choiceMatchesVehicleValue(choiceText, desiredValue, purpose, vehicle) {
    const choice = normalizeFitmentText(choiceText);
    const desired = normalizeFitmentText(desiredValue);

    if (!choice || !desired) return false;
    if (purpose === 'year') return yearChoiceMatches(choiceText, vehicle.year);
    if (choice === desired) return true;

    // Covers options such as "Model Y 2020-2024" while avoiding a loose match
    // between F-150 and F-150 Lightning.
    if (purpose === 'model' && choice.startsWith(`${desired} `)) {
      const expression = getYearExpression(choiceText);
      return expression ? yearChoiceMatches(choiceText, vehicle.year) : false;
    }

    return false;
  }

  function getModelChoiceScore(choiceText, vehicle) {
    const choice = normalizeFitmentText(choiceText);
    const desired = normalizeFitmentText(vehicle.model);
    const desiredMake = normalizeFitmentText(vehicle.make);

    if (!choice || !desired) return -1;

    const combinedVehicle = splitKnownMakePrefix(choiceText);
    const choiceMake = normalizeFitmentText(combinedVehicle?.make);
    const choiceModel = normalizeFitmentText(
      combinedVehicle?.model || choiceText
    );

    if (choiceMake && choiceMake !== desiredMake) return -1;

    const expression = getYearExpression(choiceText);
    if (expression && !yearChoiceMatches(choiceText, vehicle.year)) return -1;

    if (choiceModel === desired) return 120;
    if (choiceModel.includes(desired)) return 110;

    const aliases = cleanValue(combinedVehicle?.model || choiceText)
      .split('/')
      .map(normalizeFitmentText)
      .filter(Boolean);

    if (aliases.includes(desired)) return 115;

    // Prefer a precise F-150 Lightning choice over the earlier F-150 choice,
    // but still support grouped values such as F-250/350/450.
    const desiredNumbers = desired.match(/\b\d{3,4}\b/g) || [];
    const numberMatches = desiredNumbers.filter((number) =>
      choiceModel.includes(number)
    ).length;

    if (numberMatches) {
      const hasDistinctiveWords = desired
        .split(' ')
        .filter((word) => word.length > 3 && !/^\d+$/.test(word))
        .filter((word) => choiceModel.includes(word)).length;

      return 70 + numberMatches * 5 + hasDistinctiveWords * 4;
    }

    const broadFamilies = [
      'silverado', 'sierra', 'chassis cab', 'super duty',
      'duty', 'tundra', 'titan', 'cybertruck'
    ];

    if (
      broadFamilies.some(
        (family) => desired.includes(family) && choiceModel.includes(family)
      )
    ) {
      return 60;
    }

    if (desired.includes(choiceModel) && choiceModel.length >= 5) return 45;

    // Some products (for example Toyota RAV4) use a single "Model" option
    // group whose values contain only drivetrain/generation and a year range:
    // "Gas (5th Gen) 2019-2025". The product itself already establishes the
    // model, so the year range is the compatibility signal in this case.
    // Without this fallback the automatic matcher retries forever and leaves
    // the picker in its loading/locked state.
    if (
      expression &&
      yearChoiceMatches(choiceText, vehicle.year) &&
      !looksLikeVehicleModel(choiceModel)
    ) {
      return 25;
    }

    return -1;
  }

  function findBestVehicleChoice(choices, purpose, desiredValue, vehicle) {
    if (purpose !== 'model') {
      return choices.find((choice) =>
        choiceMatchesVehicleValue(
          choice.textContent,
          desiredValue,
          purpose,
          vehicle
        )
      );
    }

    return choices
      .map((choice) => ({
        choice,
        score:
          getModelChoiceScore(choice.textContent, vehicle) +
          (isChoiceSelected(choice) &&
          getModelChoiceScore(choice.textContent, vehicle) >= 0
            ? 100
            : 0)
      }))
      .filter((candidate) => candidate.score >= 0)
      .sort((a, b) => b.score - a.score)[0]?.choice;
  }

  function clickVariantChoice(choice) {
    if (!choice || isChoiceSelected(choice)) return false;

    if (choice instanceof HTMLOptionElement) {
      const select = choice.closest('select');
      if (!select || choice.disabled) return false;

      markFitmentUpdating();
      select.value = choice.value;
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    const input = getChoiceInput(choice);

    if (input && input.disabled) return false;

    markFitmentUpdating();
    choice.click();
    return true;
  }

  function markFitmentUpdating(duration = 280) {
    fitmentUpdatingUntil = Math.max(
      fitmentUpdatingUntil,
      now() + duration
    );

    if (fitmentTransitionTimer) {
      window.clearTimeout(fitmentTransitionTimer);
    }

    fitmentTransitionTimer = window.setTimeout(() => {
      fitmentTransitionTimer = null;
      scheduleRender();
    }, Math.max(0, fitmentUpdatingUntil - now()) + 16);

    scheduleRender();
  }

  function resetVariantSync(key) {
    if (variantSyncTimer) {
      window.clearTimeout(variantSyncTimer);
      variantSyncTimer = null;
    }

    if (variantStabilityTimer) {
      window.clearTimeout(variantStabilityTimer);
      variantStabilityTimer = null;
    }

    variantSyncKey = key;
    variantSyncAttempts = 0;
    variantSyncComplete = false;
    variantSyncCancelled = false;
  }

  function scheduleVariantStabilityCheck(context, step = 0) {
    const delays = [180, 360, 700, 1200];

    if (step >= delays.length || variantSyncCancelled) return;

    if (variantStabilityTimer) {
      window.clearTimeout(variantStabilityTimer);
    }

    variantStabilityTimer = window.setTimeout(() => {
      variantStabilityTimer = null;

      const liveContext = getContext({ touch: false });
      const productPath = getProductPath(window.location.href);
      const vehicle = normalizeVehicle(liveContext?.vehicle);

      if (
        !vehicle ||
        !canPersonalizeCurrentPdp(liveContext, productPath) ||
        variantSyncCancelled
      ) {
        return;
      }

      const picker = getVariantPicker();

      if (!picker || !pickerMatchesVehicle(picker, vehicle)) {
        variantSyncComplete = false;
        variantSyncAttempts = 0;
        scheduleRender();
        scheduleVariantSync(liveContext, 0);
        return;
      }

      scheduleRender();
      scheduleVariantStabilityCheck(context, step + 1);
    }, delays[step]);
  }

  function buildVehicleSelectionPlan(
    picker,
    vehicle,
    productPath = getProductPath(window.location.href)
  ) {
    const fieldsets = Array.from(
      picker.querySelectorAll('fieldset.variant-picker__option, fieldset')
    );
    const targets = [
      ['make', vehicle.make],
      ['model', vehicle.model],
      ['year', vehicle.year]
    ];

    const productSize = getProductSize(productPath, vehicle);
    if (productSize) targets.push(['size', productSize]);
    const plan = targets
      .map(([purpose, desiredValue]) => {
        const fieldset = fieldsets.find(
          (candidate) => getOptionPurpose(candidate) === purpose
        );

        if (!fieldset) return null;

        const choices = getOptionChoices(fieldset);

        return {
          purpose,
          desiredValue,
          fieldset,
          choices,
          selectedChoice: choices.find(isChoiceSelected) || null,
          matchingChoice: findBestVehicleChoice(
            choices,
            purpose,
            desiredValue,
            vehicle
          ) || null
        };
      })
      .filter(Boolean);

    if (!plan.some((entry) => entry.purpose === 'model')) {
      const allChoices = fieldsets.flatMap(getOptionChoices);
      const matchingChoice = findBestVehicleChoice(
        allChoices,
        'model',
        vehicle.model,
        vehicle
      );

      if (matchingChoice) {
        const fieldset = fieldsets.find((candidate) =>
          candidate.contains(matchingChoice)
        );
        const choices = fieldset
          ? getOptionChoices(fieldset)
          : allChoices;

        plan.push({
          purpose: 'model',
          desiredValue: vehicle.model,
          fieldset,
          choices,
          selectedChoice: choices.find(isChoiceSelected) || null,
          matchingChoice
        });
      }
    }

    return plan;
  }

  function pickerMatchesVehicle(picker, vehicle) {
    const plan = buildVehicleSelectionPlan(picker, vehicle);

    if (!plan.length) return false;

    return plan.every(({ choices, selectedChoice, matchingChoice }) => {
      return Boolean(
        choices.length &&
        matchingChoice &&
        selectedChoice === matchingChoice
      );
    });
  }

  function finishVariantSync(picker, context, matched = true) {
    if (!matched) {
      variantSyncComplete = true;
      if (variantStabilityTimer) {
        window.clearTimeout(variantStabilityTimer);
        variantStabilityTimer = null;
      }
      picker?.removeAttribute('data-havnby-auto-selecting');
      picker?.removeAttribute('aria-busy');
      updateContext({
        pdpVehicle: context.vehicle,
        variantTitle: null,
        variantMatchStatus: 'unmatched'
      });
      releaseVariantPickerLock();
      scheduleRender();
      return;
    }

    variantSyncTimer = window.setTimeout(() => {
      variantSyncTimer = null;

      const freshPicker = getVariantPicker();
      const vehicle = normalizeVehicle(context.vehicle);

      if (freshPicker && vehicle && pickerMatchesVehicle(freshPicker, vehicle)) {
        variantSyncComplete = true;
        freshPicker.removeAttribute('data-havnby-auto-selecting');
        freshPicker.removeAttribute('aria-busy');
        updatePersonalizedPickerVisibility(
          context,
          getProductPath(window.location.href)
        );
        releaseVariantPickerLock();
        scheduleVariantApplication(null, 40);
        scheduleRender();
        scheduleVariantStabilityCheck(context);
        return;
      }

      variantSyncAttempts += 1;
      scheduleVariantSync(context, 0);
    }, 110);
  }

  function runVariantSync(context) {
    variantSyncTimer = null;

    const productPath = getProductPath(window.location.href);
    const vehicle = normalizeVehicle(context?.vehicle);

    if (
      !vehicle ||
      !canPersonalizeCurrentPdp(context, productPath) ||
      variantSyncComplete ||
      variantSyncCancelled
    ) {
      return;
    }

    const picker = getVariantPicker();

    if (!picker) {
      variantSyncAttempts += 1;

      if (variantSyncAttempts < MAX_VARIANT_SYNC_ATTEMPTS) {
        scheduleVariantSync(context, 80);
      } else {
        releaseVariantPickerLock();
      }

      return;
    }

    picker.setAttribute('data-havnby-auto-selecting', 'true');
    picker.setAttribute('aria-busy', 'true');

    if (variantSyncAttempts >= MAX_VARIANT_SYNC_ATTEMPTS) {
      finishVariantSync(picker, context, false);
      return;
    }

    const plan = buildVehicleSelectionPlan(picker, vehicle);
    updatePersonalizedPickerVisibility(context, productPath);
    let unresolvedTarget = false;

    for (const entry of plan) {
      const { choices, selectedChoice, matchingChoice } = entry;

      if (selectedChoice === matchingChoice && matchingChoice) continue;

      if (clickVariantChoice(matchingChoice)) {
        variantSyncAttempts += 1;
        scheduleVariantSync(context, 110);
        return;
      }

      unresolvedTarget = true;
    }

    if (
      unresolvedTarget &&
      variantSyncAttempts < MAX_UNRESOLVED_VARIANT_ATTEMPTS
    ) {
      variantSyncAttempts += 1;
      scheduleVariantSync(context, 80);
      return;
    }

    finishVariantSync(picker, context, !unresolvedTarget);
  }

  function scheduleVariantSync(context, delay = 0) {
    const productPath = getProductPath(window.location.href);
    const vehicle = normalizeVehicle(context?.vehicle);

    if (!vehicle || !canPersonalizeCurrentPdp(context, productPath)) return;

    const key = [
      productPath,
      vehicle.year,
      vehicle.make,
      vehicle.model
    ].join('|');

    if (key !== variantSyncKey) resetVariantSync(key);

    // An unmatched personalized vehicle must leave the picker usable. Do not
    // restart the same failed loop on every MutationObserver render.
    if (variantSyncComplete && context?.variantMatchStatus === 'unmatched') {
      return;
    }

    if (variantSyncComplete) {
      const picker = getVariantPicker();

      if (picker && !pickerMatchesVehicle(picker, vehicle)) {
        resetVariantSync(key);
      }
    }

    if (variantSyncComplete || variantSyncCancelled || variantSyncTimer) return;

    variantSyncTimer = window.setTimeout(() => runVariantSync(context), delay);
  }

  function applyVariantSelection(variant) {
    const context = getContext({ touch: false });
    const productPath = getProductPath(window.location.href);

    if (!canPersonalizeCurrentPdp(context, productPath)) {
      scheduleRender();
      return;
    }

    if (!variantSyncComplete && !variantSyncCancelled) {
      scheduleVariantSync(context, 0);
      return;
    }

    const selectedVariant = variant || readSelectedVariant();
    const selectedOptions = readFitmentFromSelectedOptions(context.vehicle);

    if (!selectedVariant && !selectedOptions) return;

    // Visible checked options are authoritative. Impact can update them before
    // its selected-variant JSON, which previously caused stale Year/Fits text.
    const nextVehicle = selectedOptions?.vehicle || deriveVehicleFromVariant(
      selectedVariant,
      context.vehicle
    );
    const nextVariantTitle = cleanValue(
      selectedOptions?.variantTitle ||
      selectedVariant?.public_title ||
      selectedVariant?.title
    );
    const currentVehicle = normalizeVehicle(context.pdpVehicle);

    if (
      currentVehicle &&
      currentVehicle.year === nextVehicle.year &&
      currentVehicle.make === nextVehicle.make &&
      currentVehicle.model === nextVehicle.model &&
      cleanValue(context.variantTitle) === nextVariantTitle &&
      context.variantMatchStatus === 'matched'
    ) {
      return;
    }

    updateContext({
      pdpVehicle: nextVehicle,
      variantTitle: nextVariantTitle,
      variantMatchStatus: 'matched'
    });

    scheduleRender();
  }

  function scheduleVariantApplication(variant, delay = 36) {
    if (variant) pendingVariant = variant;

    if (variantApplyTimer) {
      window.clearTimeout(variantApplyTimer);
    }

    variantApplyTimer = window.setTimeout(() => {
      variantApplyTimer = null;
      const variantToApply = pendingVariant;
      pendingVariant = null;
      applyVariantSelection(variantToApply);
    }, delay);
  }

  function updateProductPage(context) {
    const productPath = getProductPath(window.location.href);
    const product = PRODUCTS[productPath];
    const titleElement = product ? getProductTitleTextElement() : null;
    const shouldPersonalize = canPersonalizeCurrentPdp(context, productPath);

    if (!product || !titleElement) {
      updatePersonalizedPickerVisibility(null, productPath);
      removeSizeMessage();
      removeFitmentMessage();
      releaseVariantPickerLock();
      return;
    }

    const directFitment = shouldPersonalize
      ? null
      : readDirectPdpFitment(productPath) ||
        readFixedProductFitment(productPath, titleElement);

    if (!shouldPersonalize) releaseVariantPickerLock();

    if (!shouldPersonalize && !directFitment) {
      updatePersonalizedPickerVisibility(null, productPath);
      restoreElementText(titleElement);
      removeSizeMessage();
      removeFitmentMessage();
      releaseVariantPickerLock();

      document
        .querySelectorAll(CONFIG.addToCartFormSelector)
        .forEach(removeVehicleProperties);

      return;
    }

    const vehicle = shouldPersonalize
      ? context.vehicle
      : directFitment.vehicle;
    const variantTitle = shouldPersonalize
      ? context.variantTitle
      : directFitment.variantTitle;
    const qualifier = shouldPersonalize
      ? ''
      : getVariantQualifier(variantTitle, vehicle);
    const displayTitle = [
      getDisplayTitle(product, vehicle),
      qualifier
    ].filter(Boolean).join(' — ');

    setElementText(titleElement, displayTitle);

    const productSize = getProductSize(productPath, vehicle);
    if (productSize) {
      const sizeMessage = ensureSizeMessage();
      const sizeText = sizeMessage.querySelector('[data-havnby-size-text]');

      if (sizeText) sizeText.textContent = `Size: ${productSize}`;
    } else {
      removeSizeMessage();
    }

    const fit = ensureFitmentMessage(titleElement);
    const fitText = fit.querySelector('[data-havnby-fit-text]');
    const fitmentIsUpdating = now() < fitmentUpdatingUntil;

    if (fitText) {
      if (fitmentIsUpdating) {
        fit.dataset.state = 'updating';
        fitText.textContent = 'Updating fit…';
      } else if (shouldPersonalize && context.variantMatchStatus === 'unmatched') {
        fit.dataset.state = 'attention';
        fitText.textContent =
          `Select a compatible option for your ${context.vehicle.year} ` +
          `${context.vehicle.make} ${context.vehicle.model}`;
      } else {
        fit.dataset.state = 'ready';
        // Fitment describes the vehicle only. Product-specific variant
        // qualifiers such as "1 Pad" belong in the title, not in the
        // customer-facing vehicle confirmation line.
        fitText.textContent = `Fits your ${getVehicleLabel(vehicle)}`;
      }
    }

    document
      .querySelectorAll(CONFIG.addToCartFormSelector)
      .forEach((form) => addVehicleProperties(form, vehicle, variantTitle));

    updatePersonalizedPickerVisibility(context, productPath);
  }

  function render() {
    const context = getContext();
    const collectionVehicle = canPersonalizeCurrentCollection(context)
      ? context.vehicle
      : null;

    updateCollectionTitle(collectionVehicle);
    updateProductCards(collectionVehicle);
    updateProductPage(context);
    scheduleVariantSync(context);
  }

  function scheduleRender() {
    if (renderScheduled) return;

    renderScheduled = true;

    window.requestAnimationFrame(() => {
      renderScheduled = false;
      render();
    });
  }

  function isEasySearchField(target) {
    return (
      target instanceof Element &&
      Object.values(CONFIG.fieldIds).includes(target.id)
    );
  }

  // A trusted change means the customer interacted with the widget. This avoids
  // activating personalization when an app merely initializes prefilled fields.
  document.addEventListener('change', (event) => {
    if (!event.isTrusted || !isEasySearchField(event.target)) return;

    window.setTimeout(() => {
      if (saveAndActivateVehicle()) scheduleRender();
    }, 0);
  });

  // Save once more in capture phase before EasySearch performs its redirect.
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest(CONFIG.searchButtonSelector)) {
        saveAndActivateVehicle({ deferCollectionScope: true });
        return;
      }

      const productLink = target.closest('a[href*="/products/"]');

      if (productLink) {
        const productPath = getProductPath(productLink.href);
        const context = getContext({ touch: false });
        const cameFromProductCard = Boolean(
          productLink.closest('product-card')
        );

        if (
          PRODUCTS[productPath] &&
          cameFromProductCard &&
          canPersonalizeCurrentCollection(context)
        ) {
          addVehicleToProductLink(productLink, context.vehicle);
          updateContext({
            productPath,
            pdpMode: 'filtered',
            pdpVehicle: null,
            variantTitle: null,
            variantMatchStatus: null
          });
        } else if (context) {
          updateContext({
            productPath: null,
            pdpMode: 'standard',
            pdpVehicle: null,
            variantTitle: null,
            variantMatchStatus: null
          });
        }

        return;
      }

      // Any navigation originating in the site header is a new, ordinary
      // entry point. Do not let a vehicle selected in EasySearch leak into
      // Shop/Popular Vehicles/Explore Havnby destinations when the customer
      // clicks the menu immediately after arriving on a PDP. The remembered
      // local vehicle is intentionally retained for the next EasySearch use;
      // only the active session personalization is deactivated.
      const headerNavigation = target.closest(
        'header, .shopify-section--header'
      );

      if (headerNavigation) {
        const context = getContext({ touch: false });

        if (context) {
          updateContext({
            collectionPath: '__standard__',
            productPath: null,
            pdpMode: 'standard',
            pdpVehicle: null,
            variantTitle: null,
            variantMatchStatus: null
          });
        }
      }

    },
    true
  );

  // Inject properties at submit time too, so newly replaced Impact forms cannot
  // submit before the observer has processed them.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches(CONFIG.addToCartFormSelector)) return;

      const context = getContext();
      const productPath = getProductPath(window.location.href);

      if (canPersonalizeCurrentPdp(context, productPath)) {
        if (context.variantMatchStatus === 'unmatched') {
          event.preventDefault();
          event.stopImmediatePropagation();

          const titleElement = getProductTitleTextElement();
          const fit = titleElement ? ensureFitmentMessage(titleElement) : null;
          const fitText = fit?.querySelector('[data-havnby-fit-text]');

          if (fit && fitText) {
            fit.dataset.state = 'attention';
            fitText.textContent =
              `Please select a compatible option for your ${context.vehicle.year} ` +
              `${context.vehicle.make} ${context.vehicle.model}`;
            fit.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }

          return;
        }

        addVehicleProperties(form, context.vehicle, context.variantTitle);
      } else {
        const directTitle = PRODUCTS[productPath]
          ? getProductTitleTextElement()
          : null;
        const directFitment = directTitle
          ? readDirectPdpFitment(productPath) ||
            readFixedProductFitment(productPath, directTitle)
          : null;

        if (directFitment) {
          addVehicleProperties(
            form,
            directFitment.vehicle,
            directFitment.variantTitle
          );
        } else {
          removeVehicleProperties(form);
        }
      }
    },
    true
  );

  function initialize() {
    claimVehicleFromProductUrl();
    render();

    const root = document.querySelector(CONFIG.mainSelector) || document.body;

    observer = new MutationObserver(scheduleRender);
    observer.observe(root, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', scheduleRender);
  window.addEventListener('pageshow', scheduleRender);

  // Impact emits variant:change with the selected variant. The generic change
  // and click fallbacks cover customized pickers and popover-style options.
  document.addEventListener('variant:change', (event) => {
    scheduleVariantApplication(event.detail?.variant, 24);
  });

  document.addEventListener('change', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('variant-picker')) return;

    if (event.isTrusted) {
      variantSyncCancelled = true;
      if (variantStabilityTimer) {
        window.clearTimeout(variantStabilityTimer);
        variantStabilityTimer = null;
      }
      markFitmentUpdating();
    }

    scheduleVariantApplication(null, 36);
  });

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('variant-picker')) return;
    if (target.closest('button.select[aria-controls]')) return;

    if (event.isTrusted) {
      variantSyncCancelled = true;
      if (variantStabilityTimer) {
        window.clearTimeout(variantStabilityTimer);
        variantStabilityTimer = null;
      }
      markFitmentUpdating();
    }

    scheduleVariantApplication(null, 72);
  });

  window.HavnbyVehicleContext = Object.freeze({
    initialized: true,
    version: RUNTIME_VERSION,
    getVehicle: getRememberedVehicle,
    getContext,
    clear: clearVehicleContext,
    render: scheduleRender,
    destroy() {
      observer?.disconnect();
      observer = null;
      if (variantSyncTimer) window.clearTimeout(variantSyncTimer);
      if (variantStabilityTimer) window.clearTimeout(variantStabilityTimer);
      if (variantPickerLockTimer) window.clearTimeout(variantPickerLockTimer);
      if (variantApplyTimer) window.clearTimeout(variantApplyTimer);
      if (fitmentTransitionTimer) window.clearTimeout(fitmentTransitionTimer);
      variantSyncTimer = null;
      variantStabilityTimer = null;
      variantPickerLockTimer = null;
      variantApplyTimer = null;
      fitmentTransitionTimer = null;
    }
  });
})();
